import { useEffect, useRef } from 'react';

export interface UseStepHistorySyncParams {
  /** Step index currently in the URL (`?step=`). */
  stepParam: number;
  /** Step index the stepper is rendering. */
  currentStep: number;
  /** Total number of steps — out-of-range URL values are clamped to the last one. */
  stepCount: number;
  goToStep: (step: number) => void;
  /** Moves the form's `currentStep` field and re-runs validation, as Next / Back do. */
  revalidateForStep: (step: number) => Promise<void>;
}

/**
 * Applies browser Back / Forward navigation to the stepper.
 *
 * Step changes made by the Next / Back buttons write `?step=` with `pushIn`, so
 * each step is its own history entry. This hook is the return path: when the
 * browser moves between those entries, the stepper follows and the form is
 * re-validated for the target step — the Yup schema branches on `currentStep`,
 * so without the revalidation the step would render against the previous
 * step's rules.
 *
 * The "previous value" ref is initialised to the mount-time `stepParam` so a
 * pre-existing `?step=` doesn't re-apply on first render (deep links are
 * already resolved before the stepper mounts).
 */
const useStepHistorySync = ({
  stepParam,
  currentStep,
  stepCount,
  goToStep,
  revalidateForStep,
}: UseStepHistorySyncParams): void => {
  const prevStepParamRef = useRef(stepParam);

  useEffect(() => {
    const prev = prevStepParamRef.current;
    prevStepParamRef.current = stepParam;
    if (prev === stepParam) return;

    const target = Math.min(Math.max(stepParam, 0), stepCount - 1);
    if (target === currentStep) return;

    goToStep(target);
    void revalidateForStep(target);
  }, [stepParam, currentStep, stepCount, goToStep, revalidateForStep]);
};

export default useStepHistorySync;
